import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setAuthTokens } from 'src/helpers/storage';
import { apiClient } from 'src/libs/axios';
import { MutationOptions } from 'src/libs/react-query';
import { authKeys } from './auth-keys';
import { notification } from 'antd';
import { useAppTranslation } from "src/i18n/hooks";
import { useWalletSelector } from '@near-wallet-selector/react-hook';

export interface LoginDto {
  signature: string;
  address: string;
  message: string;
  referrerCode?: string;
  chainId?: number;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  isNewUser: boolean;
}

export const login = async (loginDto: LoginDto) => {
  const response = await apiClient.post<LoginResponse>('/api/v2/auth/login', loginDto, {
    baseURL: process.env.REACT_APP_AUTH_SERVICE_ENDPOINT,
  });

  return response.data;
};

export type UseLoginOptions<TError = Error, TContext = unknown> = MutationOptions<
  LoginResponse,
  TError,
  LoginDto,
  TContext
>;

export const useLogin = <TError = Error, TContext = unknown>(
  options?: UseLoginOptions<TError, TContext>,
) => {
  const { signedAccountId } = useWalletSelector();
  const queryClient = useQueryClient();
  const { t } = useAppTranslation("notifications");


  return useMutation({
    mutationFn: login,
    onSuccess(data) {
      setAuthTokens(data);

      return queryClient.invalidateQueries({
        queryKey: authKeys.authenticated(signedAccountId ?? ''),
      });
    },
    onError(error: any) {
      if (error?.response?.status !== 500) {
        notification.error({
          message: t('failedToLogin'),
          description: error?.response?.data?.message || t('unexpectedError'),
          placement: 'bottomRight',
          style: { fontFamily: 'Poppins' },
        });
      }
    },
    ...options,
  });
};
